// A truck's / CDL truck's / trailer's papers (Pat, Oct 9 2026): insurance, registration, amber
// permit, oversize permit, IFTA and annual inspection. Each has its own box — drag the document onto
// it (or click to pick one) and set its expiration date. Every copy is kept; the newest is current.
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import AttachButton from '../../components/AttachButton';

// The categories that carry papers.
export const PAPER_CATEGORIES = ['Trucks', 'CDL Trucks', 'Trailers'];
const ICON = { insurance: '🛡️', registration: '🪪', amber_permit: '🟠', oversize_permit: '📏', ifta: '⛽', annual_inspection: '🔍' };

const fileUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
const parseDay = s => (s ? new Date(String(s).slice(0, 10) + 'T12:00:00') : null);
const fmtDay = s => { const d = parseDay(s); return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''; };
const fmtWhen = s => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
// Expired → red; within 30 days → red too, saying how many; within 60 → amber; else green.
export function paperState(expires) {
  if (!expires) return { label: 'No expiration set', color: '#6b7280', bg: '#f3f4f6', edge: '#e5e7eb' };
  const d = parseDay(expires); const today = new Date(); today.setHours(12, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { label: `Expired ${fmtDay(expires)}`, color: '#b91c1c', bg: '#fee2e2', edge: '#fecaca', bad: true };
  if (days === 0) return { label: 'Expires today', color: '#b91c1c', bg: '#fee2e2', edge: '#fecaca', bad: true };
  if (days <= 30) return { label: `Expires in ${days} day${days === 1 ? '' : 's'}`, color: '#b91c1c', bg: '#fef2f2', edge: '#fecaca', soon: true };
  if (days <= 60) return { label: `Expires in ${days} days`, color: '#92400e', bg: '#fffbeb', edge: '#fde68a' };
  return { label: `Good until ${fmtDay(expires)}`, color: '#15803d', bg: '#f0fdf4', edge: '#bbf7d0' };
}

async function send(eqId, body, file) {
  const fd = new FormData();
  Object.entries(body).forEach(([k, v]) => fd.append(k, v == null ? '' : v));
  if (file) fd.append('file', file);
  const r = await fetch(`${API_BASE}/equipment/${eqId}/papers`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'That did not save.');
  return r.json();
}

function PaperBox({ eqId, p, canEdit, onChanged }) {
  const cur = p.current;
  const st = paperState(cur && cur.expires_on);
  const [date, setDate] = useState(cur && cur.expires_on ? cur.expires_on : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showOld, setShowOld] = useState(false);
  useEffect(() => { setDate(cur && cur.expires_on ? cur.expires_on : ''); }, [cur && cur.id, cur && cur.expires_on]); // eslint-disable-line react-hooks/exhaustive-deps
  async function upload(files) {
    setBusy(true); setErr('');
    try { await send(eqId, { doc_type: p.type, expires_on: date && (!cur || cur.expires_on !== date) ? date : '' }, files[0]); await onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  async function saveDate(v) {
    if ((cur && cur.expires_on ? cur.expires_on : '') === v) return;
    if (!cur && !v) return;
    setBusy(true); setErr('');
    try { await send(eqId, { doc_type: p.type, expires_on: v }); await onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  async function remove(d) {
    if (!window.confirm(`Remove this copy of the ${p.label}${d.original_name ? ` (${d.original_name})` : ''}?`)) return;
    try { await apiFetch(`/equipment/${eqId}/papers/${d.id}`, { method: 'DELETE' }); await onChanged(); } catch (e) { setErr(e?.error || 'Could not remove it.'); }
  }
  const needsDate = cur && cur.filename && !cur.expires_on;
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${st.bad || st.soon ? st.edge : '#e5e7eb'}`, borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>{ICON[p.type]}</span>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: '#111827', flex: 1 }}>{p.label}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: st.color, background: st.bg, border: `1px solid ${st.edge}`, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{st.label}</span>
      </div>
      {cur && cur.filename ? (
        <a href={fileUrl(cur.filename)} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', minWidth: 0 }}>
          <span style={{ fontSize: 16 }}>{/pdf/i.test(cur.mime_type || '') ? '📄' : /^image\//.test(cur.mime_type || '') ? '🖼️' : '📁'}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur.original_name || 'Document'}</span>
            <span style={{ display: 'block', fontSize: 10.5, color: '#9ca3af' }}>{cur.by_name || 'Someone'} · {fmtWhen(cur.created_at)}</span>
          </span>
        </a>
      ) : <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No copy on file.</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: needsDate ? '#b91c1c' : '#374151' }}>Expires</label>
        <input type="date" value={date} disabled={!canEdit || busy} onChange={e => setDate(e.target.value)} onBlur={e => saveDate(e.target.value)}
          style={{ border: `1px solid ${needsDate ? '#fca5a5' : '#d1d5db'}`, borderRadius: 8, padding: '6px 8px', fontSize: 13, background: needsDate ? '#fef2f2' : '#fff' }} />
        {needsDate && <span style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700 }}>Set the expiration date</span>}
      </div>
      {canEdit && <AttachButton accept="any" icon="📥" disabled={busy} label={busy ? 'Saving…' : cur && cur.filename ? 'Drop the new one here — or click' : 'Drop it here — or click'}
        style={{ width: '100%', padding: '12px 10px', fontSize: 12.5 }} onFiles={upload} />}
      {err && <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
      {p.older.length > 0 && (<>
        <button onClick={() => setShowOld(v => !v)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{showOld ? 'Hide' : 'Show'} older copies ({p.older.length})</button>
        {showOld && p.older.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#4b5563' }}>
            {d.filename ? <a href={fileUrl(d.filename)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.original_name || 'Document'}</a> : <span style={{ flex: 1 }}>(date only)</span>}
            <span>{d.expires_on ? `exp. ${fmtDay(d.expires_on)}` : ''}</span>
            {canEdit && <button onClick={() => remove(d)} title="Remove this copy" style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>✕</button>}
          </div>
        ))}
      </>)}
    </div>
  );
}

export default function EquipmentPapers({ equipment, canEdit = false, onCount }) {
  const [papers, setPapers] = useState(null);
  const load = useCallback(() => apiFetch(`/equipment/${equipment.id}/papers`).then(r => { setPapers(r); if (onCount) onCount(r); }).catch(() => setPapers([])), [equipment.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (papers === null) return <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
      {papers.map(p => <PaperBox key={p.type} eqId={equipment.id} p={p} canEdit={canEdit} onChanged={load} />)}
    </div>
  );
}
